/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Spin,
  Select,
  DatePicker,
  Tag,
  Cascader,
  Radio,
} from 'antd';
import type { DatePickerProps, GetProp, CascaderProps } from 'antd';
// import { type RangePickerProps } from 'antd/es/date-picker';
import dayjs, { type Dayjs } from 'dayjs';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import {
  craeteNewPledgeAction,
  getPledgeTypesAction,
} from '../store/pledgeActions';
import {
  searchLoanNumbersAction,
  // getLoanByLoanNumberAction,
} from '../../Loan/store/loanActions';
import {
  type PledgeItems,
  type BankInterest,
  // type Metal,
  // type BankScheme,
  // type Interest,
  type PledgeRequest,
} from '../store/pledgeTypes';
// import { type LoanItem } from '../../Loan/store/loanTypes';
import PledgeItemDescription from '../PledgeItemDescription';
import { useNavigate } from 'react-router-dom';
import ImageUpload from '../../../components/ImageUpload';
// import WebcamImage from '../../../components/ImageUpload/webCamUpload';
// import ItemDescription from '../PledgeItemDescription';
// import { showErrorMessage } from '../../../components/ToastComponent/store/toastMiddleware';

interface SelectOption {
  label: string;
  value: string;
}

interface SelectOption2 {
  label: string;
  value: string;
  totalMonths: string;
  schemeLimit: null | string;
  minGoldRate?: number;
  documentChargeType?: string;
  documentCharge?: number;
  calculationInterestType?: string;
  appraisalChargeType?: string;
  appraisalCharge?: number;
  accountNames?: Array<{ _id: string; name: string }>;
  firstSlab?: string;
  displayLabel?: React.ReactNode;
}

const { TextArea } = Input;

interface Option {
  value: string | number;
  label: string;
  children?: Option[];
}

type DefaultOptionType = GetProp<CascaderProps, 'options'>[number];

export default function PledgeFormIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const [form] = Form.useForm();

  // const { pledgeMetal }: any = useSelector<any>((state) => state.pledge);
  const { bankScheme }: any = useSelector<any>((state) => state.pledge);

  const [bankOptions, setBankOptions] = useState<SelectOption[]>([]);
  // const [metalOptions, setMetalOptions] = useState<SelectOption[]>([]);
  const [interestOptions, setInterestOptions] = useState<SelectOption2[]>([]);
  const [accountNameOptions, setAccountNameOptions] = useState<
    Array<{ label: string; value: string; id: string }>
  >([]);

  const [loanData, setLoanData] = useState<any>([]);
  const [branch, setBranch] = useState<string>('');
  const [obName, setObName] = useState<string>('');

  // const [loanNumber, setLoanNumber] = useState<string>('');
  const [loanNumbers, setLoanNumbers] = useState<any>([]);
  const [loanItems, setLoanItems] = useState<any>([]);
  const [customerNames, setCustomerNames] = useState<string[]>([]);
  const [bank, setBank] = useState<any>('');
  const [selectedScheme, setSelectedScheme] = useState<{
    documentChargeType?: string;
    documentCharge?: number;
    appraisalChargeType?: string;
    appraisalCharge?: number;
  } | null>(null);

  // charges can be fractional, so allow sub-paisa float error
  const isChargeSplitValid = (
    online: number,
    offline: number,
    total: number
  ): boolean =>
    Math.abs(Number(online) + Number(offline) - Number(total)) <= 0.009;

  // A changed total invalidates any existing split, so fall back to fully
  // offline — the same cash/bank treatment these charges had before the split
  const applyDocumentCharge = (value: number) => {
    setDocumentChargeValue(value);
    setOnlineDocumentCharge(0);
    setOfflineDocumentCharge(value);
    setInvalidDocumentChargeSplit(false);
    form.setFieldsValue({
      documentCharge: value,
      onlineDocumentCharge: 0,
      offlineDocumentCharge: value,
    });
  };

  const applyAppraisalCharge = (value: number) => {
    setAppraisalChargeValue(value);
    setOnlineAppraisalCharge(0);
    setOfflineAppraisalCharge(value);
    setInvalidAppraisalChargeSplit(false);
    form.setFieldsValue({
      appraisalCharge: value,
      onlineAppraisalCharge: 0,
      offlineAppraisalCharge: value,
    });
  };

  const calculateCharges = (principalAmount: number) => {
    if (selectedScheme) {
      // Calculate document charge
      if (
        selectedScheme.documentChargeType &&
        selectedScheme.documentCharge !== undefined
      ) {
        let calculatedDocCharge = selectedScheme.documentCharge;
        if (selectedScheme.documentChargeType === 'Percent') {
          calculatedDocCharge =
            (principalAmount * selectedScheme.documentCharge) / 100;
        }
        applyDocumentCharge(calculatedDocCharge);
      }

      // Calculate appraisal charge
      if (
        selectedScheme.appraisalChargeType &&
        selectedScheme.appraisalCharge !== undefined
      ) {
        let calculatedAppCharge = selectedScheme.appraisalCharge;
        if (selectedScheme.appraisalChargeType === 'Percent') {
          calculatedAppCharge =
            (principalAmount * selectedScheme.appraisalCharge) / 100;
        }
        applyAppraisalCharge(calculatedAppCharge);
      }
    }
  };

  const onChange = async (value: any) => {
    setLoanItems([]);
    setCustomerNames([]);
    const flattenedArray = value.reduce(
      (accumulator: any, currentArray: any) => {
        return accumulator.concat(currentArray);
      },
      []
    );
    setLoanNumbers(flattenedArray);

    const tempLoanItems: any[] = [];
    const tempCustomerNames: any[] = [];

    await Promise.all(
      flattenedArray.map(async (x: string) => {
        // Find the data by either loanNumber or saleNumber
        const foundData = loanData.filter(
          (y: any) => y.loanNumber === x || y.saleNumber === x
        )[0];

        if (!foundData) {
          console.error(`No data found for identifier: ${x}`);
          return;
        }

        const { jewels } = foundData;
        const customer = foundData.customer;

        tempCustomerNames.push(customer.name);

        if (Boolean(jewels) && jewels.length > 0) {
          const loanItemsWithNumber = jewels.map((jewel: PledgeItems) => ({
            ...jewel,
            loanNumber: x,
          }));
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          tempLoanItems.push(...loanItemsWithNumber);
        }
      })
    );
    setCustomerNames(tempCustomerNames);
    setLoanItems(tempLoanItems);
  };

  // const addLoanNumber = (value: string) => {
  //   if (!loanNumbers.includes(value)) {
  //     form.setFieldsValue({ LoanNumber: [...loanNumbers, value] });
  //     setLoanNumbers([...loanNumbers, value]);
  //     setLoanNumber('');
  //   }
  // };

  // const removeLoanNumber = (value: string) => {
  //   const updatedLoanNumbers = [...loanNumbers.filter((x) => x !== value)];
  //   setLoanNumbers(updatedLoanNumbers);
  //   form.setFieldsValue({ LoanNumber: updatedLoanNumbers });
  // };

  const getPledgeTypes = async (): Promise<void> => {
    await getPledgeTypesAction(dispatch, { branch });
  };

  useEffect(() => {
    form.setFieldsValue({ startDate: dayjs() });
    form.setFieldsValue({ documentChargeHandling: 'dontAdd' });
  }, []);

  useEffect(() => {
    if (branch) {
      void getPledgeTypes();
      setAccountNameOptions([]);
    }
  }, [branch]);

  useEffect(() => {
    const bankOptions: Array<{ label: string; value: string }> = Object.keys(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      bankScheme
    ).map((x: string) => {
      return {
        label: x,
        value: x,
      };
    });
    setBankOptions(bankOptions);

    // const metalOptions: Array<{ label: string; value: string }> =
    //   pledgeMetal.map((x: Metal) => {
    //     return {
    //       label: x.metalName,
    //       value: x.metalName,
    //     };
    //   });
    // setMetalOptions(metalOptions);
  }, [bankScheme]);

  // interface Item {
  //   itemType: string;
  //   quantity: number;
  //   grossWeight: number;
  //   unwantedWeight: number;
  //   netWeight: number;
  //   purity: string;
  //   isPledged: boolean;
  //   _id: string;
  //   loanNumber: string;
  //   isChecked: boolean;
  // }

  // const validatePrincipalAgainstMinGoldRate = (
  //   items: Item[],
  //   principal: number,
  //   minGoldRate: number
  // ): boolean => {
  //   const totalNetWeight = items
  //     .filter((item) => item.isChecked)
  //     .reduce((sum, item) => sum + item.netWeight, 0);

  //   if (totalNetWeight <= 0) {
  //     console.log('Total net weight is zero or invalid');
  //     return false;
  //   }

  //   const ratePerGram = principal / totalNetWeight;

  //   return ratePerGram >= minGoldRate;
  // };

  const onFinish = async (values: PledgeRequest) => {
    if (isLoading) return;
    // const validateMinGoldRate = validatePrincipalAgainstMinGoldRate(
    //   loanItems,
    //   form.getFieldValue('principal'),
    //   form.getFieldValue('minGoldRate')
    // );

    // if (!validateMinGoldRate) {
    //   dispatch(
    //     showErrorMessage('Principal should be greater than Min Gold Value')
    //   );
    //   return;
    // }
    setIsLoading(true);
    const action = craeteNewPledgeAction;
    const response = await action(dispatch, {
      ...values,
      // branch: ,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      jewels: modifyItemsToJewels(loanItems),
      documentCharge: Number(form.getFieldValue('documentCharge') ?? 0),
      onlineDocumentCharge: Number(onlineDocumentCharge),
      offlineDocumentCharge: Number(offlineDocumentCharge),
      appraisalCharge: Number(form.getFieldValue('appraisalCharge') ?? 0),
      onlineAppraisalCharge: Number(onlineAppraisalCharge),
      offlineAppraisalCharge: Number(offlineAppraisalCharge),
      minGoldRate: Number(form.getFieldValue('minGoldRate')),
      principal: Number(principal),
      calculationInterestType,
      documentChargeHandling,
      bankAccountNameId,
    });
    setIsLoading(false);
    if (response !== false) {
      form.resetFields();
      setObTokenImage('');
      setObTokenImageError('');
      // setPhoto('');
      // setLoanNumbers([]);
      // setItems([]);
      setLoanNumbers([]);
      setLoanItems([]);
      setDatePickerDate(dayjs());
      setPrincipal(0);
      applyDocumentCharge(0);
      applyAppraisalCharge(0);
      setBankAccountNameId(null);
      navigate(`/pledge?pledgeID=${response}`);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
    const obTokenField = errorInfo?.errorFields?.find(
      (field: { name: string[] }) => field.name[0] === 'obTokenImage'
    );
    setObTokenImageError(obTokenField?.errors?.[0] ?? '');
  };

  const modifyItemsToJewels = (items: PledgeItems[]) => {
    const finalItems = items.filter((x) => x.isChecked && !x.isPledged);
    return finalItems.map((item) => {
      return {
        itemType: item.itemType,
        // Purity: item.purity.split('-')[0],
        grossWeight: Number(item.grossWeight),
        unwantedWeight: Number(item.unwantedWeight),
        netWeight: Number(item.netWeight),
        // PurityPercentage: Number(item.purity.split('-')[1]),
        quantity: Number(item.quantity),
        purity: item.purity,
        loanNumber: item.loanNumber,
        _id: item._id,
      };
    });
  };

  const [branchOptions, setBranchOptions] = useState<
    Array<{
      label: string;
      value: string;
    }>
  >([]);

  useEffect(() => {
    const branches = localStorage.getItem('branches');
    if (branches != null) {
      const parsedBranches = JSON.parse(branches);
      const branchOptionTemp: Array<{ label: string; value: string }> =
        parsedBranches.map((branch: { code: string; id: string }) => ({
          label: branch.code,
          value: branch.id,
        }));
      setBranchOptions(branchOptionTemp);
      if (branchOptionTemp.length > 0) {
        setBranch(branchOptionTemp[0].value);
        form.setFieldsValue({ branch: branchOptionTemp[0].value });
      }
    }
    form.setFieldsValue({ pledgeType: 'New' });
  }, []);

  useEffect(() => {}, [branch]);

  // const handleChangeMetal = (value: string): void => {
  //   const metalInfo: Metal = pledgeMetal.filter(
  //     (x: Metal) => x.metalName === value
  //   )[0];

  //   const purityOptionsMap: SelectOption[] = metalInfo?.purityQuality.map(
  //     (x, index) => {
  //       return {
  //         label: String(`${x}-${index}`),
  //         value: String(`${x}-${index}`),
  //       };
  //     }
  //   );
  //   setPurityOptions(purityOptionsMap);

  //   const metalTypesMap: SelectOption[] = metalInfo?.metalType.map((x) => {
  //     return {
  //       label: x,
  //       value: x,
  //     };
  //   });
  //   setMetalTypes(metalTypesMap);
  // };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [principal, setPrincipal] = useState<number>(0);
  const [documentChargeValue, setDocumentChargeValue] = useState<number>(0);
  const [appraisalChargeValue, setAppraisalChargeValue] = useState<number>(0);
  const [onlineDocumentCharge, setOnlineDocumentCharge] = useState<number>(0);
  const [offlineDocumentCharge, setOfflineDocumentCharge] = useState<number>(0);
  const [onlineAppraisalCharge, setOnlineAppraisalCharge] = useState<number>(0);
  const [offlineAppraisalCharge, setOfflineAppraisalCharge] =
    useState<number>(0);
  const [invalidDocumentChargeSplit, setInvalidDocumentChargeSplit] =
    useState<boolean>(false);
  const [invalidAppraisalChargeSplit, setInvalidAppraisalChargeSplit] =
    useState<boolean>(false);
  const [calculationInterestType, setCalculationInterestType] =
    useState<string>('Simple');
  const [documentChargeHandling, setDocumentChargeHandling] =
    useState<string>('dontAdd');
  const [bankAccountNameId, setBankAccountNameId] = useState<string | null>(null);
  const [obTokenImage, setObTokenImage] = useState<string>('');
  // the form's own error would sit under the Upload button, so ImageUpload shows it instead
  const [obTokenImageError, setObTokenImageError] = useState<string>('');
  // const [photo, setPhoto] = useState<string>('');
  // const [showWebCam, setShowWebCam] = useState(false);
  // const handleWebCam = (): void => {
  //   setShowWebCam(!showWebCam);
  // };
  // const [purityOptions, setPurityOptions] = useState<SelectOption[]>([]);
  // const [metalTypes, setMetalTypes] = useState<SelectOption[]>([]);
  // const [items, setItems] = useState<LoanItem[]>([]);
  // const [amountPayableToCustomer, setAmountPayableToCustomer] =
  //   useState<string>('');
  // const [customerId, setCustomerId] = useState<string>('');
  // const [loanNumber, setLoanNumber] = useState<string>('');
  // const [bank, setBank] = useState<string>('');
  // const [obNumber, setObNumber] = useState<string>('');
  // const [obAccountName, setObAccountName] = useState<string>('');
  // const [netWeight, setNetWeight] = useState<string>('');
  // const [loanAmount, setLoanAmount] = useState<string>('');
  // const [documentCharges, setDocumentCharges] = useState<string>('');
  // const [tenure, setTenure] = useState<string>('');
  // const [remarks, setRemarks] = useState<string>('');
  // const [interest, setInterest] = useState<string>('');
  const [datePickerDate, setDatePickerDate] = useState<Dayjs | null>(dayjs());
  // const [, setCreatedDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  // const [renewalDatePickerDate, setRenewalDatePickerDate] =
  //   useState<Dayjs | null>(dayjs());
  // const [, setRenewalDate] = useState<string>(dayjs().format('YYYY-MM-DD'));

  // const onChangeCreatedDate: DatePickerProps['onChange'] = (
  //   date,
  //   dateString
  // ): void => {
  //   setDatePickerDate(date);
  //   setCreatedDate(dateString);
  // };

  // const onChangeRenewalDate: DatePickerProps['onChange'] = (
  //   date,
  //   dateString
  // ): void => {
  //   setRenewalDatePickerDate(date);
  //   setRenewalDate(dateString);
  // };

  const onChangeDate: DatePickerProps['onChange'] = (
    date: Dayjs | null,
    dateString: string
  ): void => {
    form.setFieldsValue({ startDate: dateString });
    // setDob(dateString);
    setDatePickerDate(date);
  };

  // eslint-disable-next-line arrow-body-style
  // const disabledDate: RangePickerProps['disabledDate'] = (
  //   current: any
  // ): boolean => {
  //   // Can not select days before after today
  //   return current < dayjs().startOf('day');
  // };

  const handleBankChange = (value: string): void => {
    setBank(value);
    form.setFieldsValue({ interestType: null, obName: null });
    setBankAccountNameId(null);
    setObName('');
    if (!bankScheme[value]) {
      setInterestOptions([]);
      setAccountNameOptions([]);
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const schemes: BankInterest[] = (bankScheme[value] ?? []) as BankInterest[];
    const interestOptions: SelectOption2[] = schemes
      .filter((x: BankInterest) => x.isActive === true)
      .map((x: BankInterest): SelectOption2 => {
        const label = `${x.totalMonths} month scheme${
          x.schemeLimit ? ' - ' : ''
        }${x.schemeLimit ?? ''}`;
        const slab = x.interestRates?.[0];
        const firstSlab =
          slab?.startDate != null &&
          slab?.endDate != null &&
          slab?.interest != null
          ? `${slab.startDate}-${slab.endDate}: ${slab.interest}%`
          : undefined;
        return {
        label,
        firstSlab,
        displayLabel: firstSlab ? (
          <span>
            {label} <span style={{ color: '#8c8c8c' }}>({firstSlab})</span>
          </span>
        ) : (
          label
        ),
        value: x.id ?? '',
        totalMonths: String(x.totalMonths),
        schemeLimit: x.schemeLimit != null ? String(x.schemeLimit) : null,
        minGoldRate: x.minGoldRate,
        documentChargeType: x.documentChargeType,
        documentCharge: x.documentCharge,
        calculationInterestType: x.interestType as unknown as string,
        appraisalChargeType: x.appraisalChargeType,
        appraisalCharge: x.appraisalCharge,
        accountNames: x.accountNames,
        };
      });
    setInterestOptions(interestOptions);

    // Pre-populate account names with the union of all scheme accountNames for this bank
    const accountMap = new Map<string, { label: string; value: string; id: string }>();
    schemes.forEach((x: BankInterest) => {
      if (x.isActive) {
        x.accountNames?.forEach((acc) => {
          accountMap.set(acc._id, { label: acc.name, value: acc.name, id: acc._id });
        });
      }
    });
    setAccountNameOptions(Array.from(accountMap.values()));
  };

  useEffect(() => {}, [interestOptions, bankOptions]);

  const filter = (inputValue: string, path: DefaultOptionType[]) =>
    path.some((option) =>
      (option.label as string).toLowerCase().includes(inputValue.toLowerCase())
    );

  const [pledgeType, setPledgeType] = useState<string>('New');

  const onSearchLoanNumber = async (value: string) => {
    if (value !== '' && value.length === 10) {
      const data = await searchLoanNumbersAction(dispatch, value);
      if (data) {
        // Determine if this is loan or sale data
        const identifier = data.loanNumber || data.saleNumber;

        if (!identifier) {
          console.error('No loan number or sale number found in data');
          return;
        }

        // Check if this loan/sale already exists in loanData
        const existingLoanData = loanData.some(
          (item: any) =>
            item.loanNumber === identifier || item.saleNumber === identifier
        );

        if (existingLoanData) {
          const updatedLoanData = loanData.map((item: any) =>
            item.loanNumber === identifier || item.saleNumber === identifier
              ? data
              : item
          );
          setLoanData(updatedLoanData);
        } else {
          setLoanData([...loanData, data]);
        }

        setCascaderOptions(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          [
            {
              label: identifier,
              value: identifier,
            },
          ]
        );
      }
    } else {
      setCascaderOptions([]);
    }
  };

  const [cascaderOptions, setCascaderOptions] = useState<Option[]>();

  useEffect(() => {
    const refreshData = async () => {
      if (loanNumbers.length > 0) {
        const finalArray = loanNumbers.map((eachLoanNumber: string) => [
          eachLoanNumber,
        ]);
        await onChange(finalArray);
      }
    };
    void refreshData();
  }, [loanData]);

  const handleRefreshTable = async (loanNumber: string | undefined) => {
    await onSearchLoanNumber(loanNumber ?? '');
  };

  return (
    <Form
      form={form}
      name="pledge_form"
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
    >
      <div className="pledge__form">
        {/* <div className="pledge__input__container">
            <p className="pledge__input__label">Customer ID</p>
            <Input
              value={customerId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCustomerId(e.target.value);
              }}
              className="pledge__input"
              placeholder="Enter Customer ID"
            />
          </div> */}
        {/* <Form.Item
          name="LoanNumber"
          rules={[
            { required: true, message: 'Minimum 1 loan number required!' },
          ]}
        > */}
        <div className="pledge__input__container">
          <p className="pledge__input__label">Loan Number/Sale Number</p>
          {/* <Input
              value={loanNumber}
              className="pledge__input"
              placeholder="Enter Loan number"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setLoanNumber(e.target.value);
              }}
              onPressEnter={(e: any) => {
                addLoanNumber(loanNumber);
                e.preventDefault();
              }}
            /> */}
          <Cascader
            style={{ width: '100%', height: 56 }}
            options={cascaderOptions}
            onChange={(value: any) => {
              void onChange(value);
            }}
            multiple
            autoClearSearchValue
            maxTagCount={4}
            showSearch={{ filter }}
            onSearch={(value) => {
              void onSearchLoanNumber(value);
            }}
          />

          <div className="loan__tags">
            {customerNames.map((x: string) => {
              return (
                <Tag className="loan__tag" key={x}>
                  {x}
                </Tag>
              );
            })}
          </div>
        </div>
        {/* </Form.Item> */}
        <Form.Item
          name="branch"
          rules={[{ required: true, message: 'Please select branch!' }]}
        >
          <div className="pledge__input__container">
            <p className="pledge__input__label">Branch</p>
            <Select
              value={branch}
              onChange={(value: string): void => {
                form.setFieldsValue({ branch: value, bank: null, interestType: null, obName: null });
                setBranch(value);
                setInterestOptions([]);
                setAccountNameOptions([]);
                setBankAccountNameId(null);
                setObName('');
              }}
              style={{ width: 311 }}
              placeholder="Select branch"
              options={branchOptions}
              className="pledge__select"
            />
          </div>
        </Form.Item>
        <Form.Item
          name="pledgeType"
          rules={[{ required: true, message: 'Please select pledge type!' }]}
        >
          <div className="pledge__input__container">
            <p className="pledge__input__label">Pledge Type</p>
            <Select
              value={pledgeType}
              onChange={(value: string): void => {
                form.setFieldsValue({ pledgeType: value });
                setPledgeType(value);
              }}
              style={{ width: 311 }}
              placeholder="Select Pledge Type"
              options={[
                { label: 'New', value: 'New' },
                { label: 'Repledge', value: 'Repledge' },
              ]}
              className="pledge__select"
            />
          </div>
        </Form.Item>
        <Form.Item
          name="startDate"
          rules={[{ required: true, message: 'Please select start date!' }]}
        >
          <div className="pledge__input__container">
            <p className="pledge__input__label">Start Date</p>
            <DatePicker
              value={datePickerDate}
              // disabledDate={disabledDate}
              className="pledge__input"
              onChange={onChangeDate}
            />
          </div>
        </Form.Item>
        <Form.Item
          name="bank"
          rules={[{ required: true, message: 'Select Bank!' }]}
        >
          <div className="pledge__input__container">
            <p className="pledge__input__label">Bank name</p>
            <Select
              style={{ width: 311 }}
              value={bank}
              placeholder="Select interest type"
              onChange={(value: string): void => {
                form.setFieldsValue({ bank: value });
                handleBankChange(value);
              }}
              options={bankOptions}
              className="pledge__select"
            />
          </div>
        </Form.Item>
        <Form.Item name="obNumber" rules={[{ message: 'Enter OB Number!' }]}>
          <div className="pledge__input__container">
            <p className="pledge__input__label">OB Number</p>
            <Input
              className="pledge__input"
              placeholder="Enter OB Account number"
            />
          </div>
        </Form.Item>
        <Form.Item
          name="obName"
          rules={[{ required: true, message: 'Enter OB Name!!' }]}
        >
          <div className="pledge__input__container">
            <p className="pledge__input__label">OB Account Name</p>
            <Select
              style={{ width: 311 }}
              value={obName}
              placeholder="Select OB Account Name"
              onChange={(value: string, option: any): void => {
                form.setFieldsValue({ obName: value });
                setBankAccountNameId(option?.id ?? null);
                setObName(value);
              }}
              options={accountNameOptions}
              className="pledge__select"
            />
          </div>
        </Form.Item>
        <Form.Item
          name="interestType"
          rules={[{ required: true, message: 'Select Interest scheme!' }]}
        >
          <div className="pledge__input__container">
            <p className="pledge__input__label">OB ROI</p>
            <Select
              style={{ width: 311 }}
              placeholder="Select OB interest"
              onChange={(value: string, option: any): void => {
                form.setFieldsValue({
                  interestType: value,
                });
                form.setFieldsValue({ minGoldRate: option.minGoldRate });

                setCalculationInterestType(
                  option.calculationInterestType ?? 'Simple'
                );

                setSelectedScheme({
                  documentChargeType: option.documentChargeType,
                  documentCharge: option.documentCharge,
                  appraisalChargeType: option.appraisalChargeType,
                  appraisalCharge: option.appraisalCharge,
                });

                // Narrow account names to only those matching this scheme's bank+branch
                if (Array.isArray(option.accountNames) && option.accountNames.length > 0) {
                  setAccountNameOptions(
                    (option.accountNames as Array<{ _id: string; name: string }>).map((acc) => ({
                      label: acc.name,
                      value: acc.name,
                      id: acc._id,
                    }))
                  );
                }
                form.setFieldsValue({ obName: null });
                setBankAccountNameId(null);
                setObName('');

                if (
                  option.documentChargeType &&
                  option.documentCharge !== undefined
                ) {
                  let calculatedDocCharge = option.documentCharge;
                  if (option.documentChargeType === 'Percent') {
                    calculatedDocCharge =
                      (principal * option.documentCharge) / 100;
                  }
                  applyDocumentCharge(calculatedDocCharge);
                }

                if (
                  option.appraisalChargeType &&
                  option.appraisalCharge !== undefined
                ) {
                  let calculatedAppCharge = option.appraisalCharge;
                  if (option.appraisalChargeType === 'Percent') {
                    calculatedAppCharge =
                      (principal * option.appraisalCharge) / 100;
                  }
                  applyAppraisalCharge(calculatedAppCharge);
                }
              }}
              options={interestOptions}
              optionLabelProp="displayLabel"
              optionRender={(option: any) => (
                <div>
                  <div>{option.data.label}</div>
                  {option.data.firstSlab && (
                    <Tag color="blue" style={{ marginTop: 2, fontSize: 11 }}>
                      {option.data.firstSlab}
                    </Tag>
                  )}
                </div>
              )}
              className="pledge__select"
            />
          </div>
        </Form.Item>
        {/* <Form.Item
          name="JewelryMetal"
          rules={[{ required: true, message: 'Select Metal Type!' }]}
        >
          <div className="pledge__input__container">
            <p className="pledge__input__label">Metal</p>
            <Select
              style={{ width: 311 }}
              placeholder="Select metal"
              onChange={(value: string): void => {
                form.setFieldsValue({ JewelryMetal: value });
                handleChangeMetal(value);
              }}
              options={metalOptions}
              className="pledge__select"
            />
          </div>
        </Form.Item> */}
        {/* <Form.Item
          name="paymentMethod"
          rules={[{ required: true, message: 'Select Payment method' }]}
        >
          <div className="pledge__input__container">
            <p className="pledge__input__label">Payment Method</p>
            <Select
              placeholder="Select payment method"
              onChange={(value: string): void => {
                form.setFieldsValue({ paymentMethod: value });
              }}
              options={[
                { label: 'Cash', value: 'cash' },
                { label: 'Card', value: 'card' },
                { label: 'Online', value: 'online' },
                { label: 'Other', value: 'other' },
              ]}
              className="pledge__select"
            />
          </div>
        </Form.Item> */}
        {/* <Form.Item
          name="principal"
          rules={[{ required: true, message: 'Enter Principal!' }]}
        > */}
        <div className="pledge__input__container">
          <p className="pledge__input__label">Principal</p>
          <Input
            type="number"
            className="pledge__input"
            placeholder="Enter principal"
            onWheel={(e) => {
              e.currentTarget.blur();
            }}
            value={principal}
            disabled
          />
        </div>
        {/* </Form.Item> */}
        <Form.Item
          name="onlinePrincipal"
          rules={[{ required: true, message: 'Enter Online Principal!' }]}
        >
          <div className="pledge__input__container">
            <p className="pledge__input__label">Online Principal</p>
            <Input
              type="number"
              className="pledge__input"
              placeholder="Enter Online Principal"
              onWheel={(e) => {
                e.currentTarget.blur();
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                const newPrincipal =
                  Number(value) +
                  Number(form.getFieldValue('offlinePrincipal') ?? 0);
                setPrincipal(newPrincipal);
                calculateCharges(newPrincipal);
              }}
            />
          </div>
        </Form.Item>
        <Form.Item
          name="offlinePrincipal"
          rules={[{ required: true, message: 'Enter Offline Principal!' }]}
        >
          <div className="pledge__input__container">
            <p className="pledge__input__label">Offline Principal</p>
            <Input
              type="number"
              className="pledge__input"
              placeholder="Enter Offline Principal"
              onWheel={(e) => {
                e.currentTarget.blur();
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                const newPrincipal =
                  Number(value) +
                  Number(form.getFieldValue('onlinePrincipal') ?? 0);
                setPrincipal(newPrincipal);
                calculateCharges(newPrincipal);
              }}
            />
          </div>
        </Form.Item>
        <Form.Item name="documentCharge">
          <div className="pledge__input__container">
            <p className="pledge__input__label">Document charges</p>
            <Input
              type="number"
              className="pledge__input"
              placeholder="Enter document charges"
              value={documentChargeValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                applyDocumentCharge(Number(e.target.value));
              }}
              onWheel={(e) => {
                e.currentTarget.blur();
              }}
            />
          </div>
        </Form.Item>
        <Form.Item name="onlineDocumentCharge">
          <div className="pledge__input__container">
            <p className="pledge__input__label">Online Document Charge</p>
            <Input
              type="number"
              className="pledge__input"
              placeholder="Enter online document charge"
              value={onlineDocumentCharge}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = Number(e.target.value);
                setOnlineDocumentCharge(value);
                form.setFieldsValue({ onlineDocumentCharge: value });
                setInvalidDocumentChargeSplit(
                  !isChargeSplitValid(
                    value,
                    offlineDocumentCharge,
                    documentChargeValue
                  )
                );
              }}
              onWheel={(e) => {
                e.currentTarget.blur();
              }}
            />
            {invalidDocumentChargeSplit && (
              <p className="error__message">
                Online + offline must equal the document charge
              </p>
            )}
          </div>
        </Form.Item>
        <Form.Item name="offlineDocumentCharge">
          <div className="pledge__input__container">
            <p className="pledge__input__label">Offline Document Charge</p>
            <Input
              type="number"
              className="pledge__input"
              placeholder="Enter offline document charge"
              value={offlineDocumentCharge}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = Number(e.target.value);
                setOfflineDocumentCharge(value);
                form.setFieldsValue({ offlineDocumentCharge: value });
                setInvalidDocumentChargeSplit(
                  !isChargeSplitValid(
                    onlineDocumentCharge,
                    value,
                    documentChargeValue
                  )
                );
              }}
              onWheel={(e) => {
                e.currentTarget.blur();
              }}
            />
            {invalidDocumentChargeSplit && (
              <p className="error__message">
                Online + offline must equal the document charge
              </p>
            )}
          </div>
        </Form.Item>
        <Form.Item name="appraisalCharge">
          <div className="pledge__input__container">
            <p className="pledge__input__label">Appraisal Charge</p>
            <Input
              type="number"
              className="pledge__input"
              placeholder="Enter appraisal charge"
              value={appraisalChargeValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                applyAppraisalCharge(Number(e.target.value));
              }}
              onWheel={(e) => {
                e.currentTarget.blur();
              }}
            />
          </div>
        </Form.Item>
        <Form.Item name="onlineAppraisalCharge">
          <div className="pledge__input__container">
            <p className="pledge__input__label">Online Appraisal Charge</p>
            <Input
              type="number"
              className="pledge__input"
              placeholder="Enter online appraisal charge"
              value={onlineAppraisalCharge}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = Number(e.target.value);
                setOnlineAppraisalCharge(value);
                form.setFieldsValue({ onlineAppraisalCharge: value });
                setInvalidAppraisalChargeSplit(
                  !isChargeSplitValid(
                    value,
                    offlineAppraisalCharge,
                    appraisalChargeValue
                  )
                );
              }}
              onWheel={(e) => {
                e.currentTarget.blur();
              }}
            />
            {invalidAppraisalChargeSplit && (
              <p className="error__message">
                Online + offline must equal the appraisal charge
              </p>
            )}
          </div>
        </Form.Item>
        <Form.Item name="offlineAppraisalCharge">
          <div className="pledge__input__container">
            <p className="pledge__input__label">Offline Appraisal Charge</p>
            <Input
              type="number"
              className="pledge__input"
              placeholder="Enter offline appraisal charge"
              value={offlineAppraisalCharge}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = Number(e.target.value);
                setOfflineAppraisalCharge(value);
                form.setFieldsValue({ offlineAppraisalCharge: value });
                setInvalidAppraisalChargeSplit(
                  !isChargeSplitValid(
                    onlineAppraisalCharge,
                    value,
                    appraisalChargeValue
                  )
                );
              }}
              onWheel={(e) => {
                e.currentTarget.blur();
              }}
            />
            {invalidAppraisalChargeSplit && (
              <p className="error__message">
                Online + offline must equal the appraisal charge
              </p>
            )}
          </div>
        </Form.Item>
        {/* <div className="pledge__input__container">
            <p className="pledge__input__label">Created Date</p>
            <DatePicker
              disabledDate={disabledDate}
              value={datePickerDate}
              className="pledge__input"
              onChange={onChangeCreatedDate}
            />
          </div> */}
        {/* <div className="pledge__input__container">
            <p className="pledge__input__label">Tenure</p>
            <Input
              value={tenure}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTenure(e.target.value);
              }}
              className="pledge__input"
              placeholder="Enter tenure"
            />
          </div>
          <div className="pledge__input__container">
            <p className="pledge__input__label">Renewal Date</p>
            <DatePicker
              disabledDate={disabledDate}
              value={renewalDatePickerDate}
              className="pledge__input"
              onChange={onChangeRenewalDate}
            />
          </div> */}
        <Form.Item
          name="remarks"
          rules={[{ required: true, message: 'Enter remarks!' }]}
        >
          <div className="pledge__input__container">
            <p className="pledge__input__label">Pledge remarks</p>
            <TextArea
              rows={3}
              className="pledge__textarea"
              placeholder="Enter remarks"
            />
          </div>
        </Form.Item>
        <Form.Item
          name="obTokenImage"
          noStyle
          rules={[{ required: true, message: 'Please upload the OB token image' }]}
        >
          <div className="pledge__photo__container">
            <p className="pledge__input__label">OB Token Image</p>
            <p className="pledge__photo__sub__title">
              Image format .jpg .jpeg .png
            </p>
            <p className="pledge__photo__sub__title">
              Minimum size 300px x 300px
            </p>
            <ImageUpload
              onImage={(image: string) => {
                form.setFieldsValue({ obTokenImage: image });
                setObTokenImage(image);
                setObTokenImageError('');
              }}
              src={obTokenImage}
              errorMessage={obTokenImageError}
            />
          </div>
        </Form.Item>
        {/* <div className="pledge__photo__container">
          <p className="pledge__input__label">Image</p>
          <p className="pledge__photo__sub__title">
            Image format .jpg .jpeg .png
          </p>
          <p className="pledge__photo__sub__title">
            Minimum size 300px x 300px
          </p>
          <ImageUpload
            onImage={(image: string) => {
              form.setFieldsValue({ Photo: image });
              setPhoto(image);
            }}
            src={photo}
          />
          {!showWebCam && (
            <p className="pledge__button" onClick={handleWebCam}>
              Start Webcam
            </p>
          )}
          {showWebCam && (
            <WebcamImage
              name="photo"
              onChange={(e: { target: { name: string; value: string } }) => {
                form.setFieldsValue({ Photo: e?.target?.value });
                setPhoto(e?.target?.value);
              }}
            />
          )}
        </div> */}
      </div>
      <Form.Item
        name="documentChargeHandling"
        rules={[
          {
            required: true,
            message: 'Please select document charge handling option!',
          },
        ]}
        style={{ width: '100%', padding: '0 20px' }}
      >
        <div style={{ width: '100%', marginBottom: 20 }}>
          <p
            style={{
              fontSize: 16,
              fontWeight: 600,
              marginBottom: 12,
              color: '#333',
            }}
          >
            Document Charge Handling
          </p>
          <Radio.Group
            value={documentChargeHandling}
            onChange={(e) => {
              const value = e.target.value;
              setDocumentChargeHandling(value);
              form.setFieldsValue({ documentChargeHandling: value });
            }}
            style={{ width: '100%' }}
          >
            <Radio
              value="addToPrincipal"
              style={{ display: 'block', marginBottom: 12 }}
            >
              Add Document Charge to Principal
            </Radio>
            <Radio
              value="collectOnClose"
              style={{ display: 'block', marginBottom: 12 }}
            >
              Collect Document Charge when closing Pledge
            </Radio>
            <Radio
              value="dontAdd"
              style={{ display: 'block', marginBottom: 12 }}
            >
              Don&apos;t add document charge to principal (Default)
            </Radio>
          </Radio.Group>
        </div>
      </Form.Item>
      <div className="pledge__items__section">
        <p className="pledge__items__title">Item Description</p>
        <PledgeItemDescription
          items={loanItems}
          refreshTable={handleRefreshTable}
          updateItems={(newItems) => {
            setLoanItems(newItems);
          }}
        />
      </div>
      <div className="pledge__actions__section">
        <Button
          onClick={() => {
            form.resetFields();
            setObTokenImage('');
            setObTokenImageError('');
            applyDocumentCharge(0);
            applyAppraisalCharge(0);
            setBankAccountNameId(null);
            // setPhoto('');
          }}
          className="pledge__cancel__button"
        >
          Cancel
        </Button>
        <Button
          disabled={
            isLoading ||
            invalidDocumentChargeSplit ||
            invalidAppraisalChargeSplit ||
            !loanNumbers.length ||
            !loanItems.filter((x: PledgeItems) => x.isChecked).length
          }
          htmlType="submit"
          className="pledge__save__button"
        >
          {isLoading ? (
            <Spin
              indicator={
                <LoadingOutlined
                  style={{ fontSize: 24, color: 'white' }}
                  spin
                />
              }
            />
          ) : (
            <span>Create New Pledge</span>
          )}
        </Button>
      </div>
    </Form>
  );
}
